'use client'

import { useTranslations } from 'next-intl'

import { useClubFinance } from '@/features/clubs/hooks/useClubMonetization'
import { formatLeaderboardNumber } from '@/features/leaderboard/formatLeaderboardNumber'
import { useReferralLink, useReferralsStats } from '@/features/referrals/hooks'
import { showToast } from '@/providers/ToastProvider/ToastMessage'
import { StatCard } from '@/shared/ui'
import { CopyLinkIcon } from '@/ui/icons/CopyLinkIcon'

import S from './ClubMonetization.module.scss'
import FanActivity from './FanActivity'
import WithdrawHistory from './WithdrawHistory'

const appUrl = (process.env.NEXT_PUBLIC_APP_URL || '').replace(/\/+$/, '')

const ReferralCard = () => {
  const t = useTranslations('club.monetization')
  const tCard = useTranslations('club.card')
  const { data: link } = useReferralLink()
  const { data: stats } = useReferralsStats()
  const code = link?.code?.trim()
  const url =
    link?.link?.trim() ||
    (code ? `${appUrl}/r/${encodeURIComponent(code)}` : '')

  const copy = async () => {
    if (!url) {
      return
    }
    await navigator.clipboard.writeText(url)
    showToast({ type: 'success', title: tCard('copiedReferralLinkSuccess') })
  }

  return (
    <section className={S.referral} aria-label={t('referralLink')}>
      <header className={S.referralHeader}>
        <span className={S.cardTitle}>{t('referralLink')}</span>
        <span className={S.hint}>{t('referralHint')}</span>
      </header>
      <div className={S.linkField}>
        <span className={S.linkText}>{url || '—'}</span>
        <button
          type="button"
          className={S.iconButton}
          onClick={copy}
          disabled={!url}
          aria-label={t('copyLink')}
        >
          <CopyLinkIcon />
        </button>
      </div>
      <span className={S.referralCount}>
        {t('totalReferrals', { count: stats?.total_invited ?? 0 })}
      </span>
    </section>
  )
}

const ap = (value?: number) => `${formatLeaderboardNumber(value ?? 0)} AP`

interface ClubMonetizationProps {
  /** Withdrawals open once the club / athlete is activated. */
  canWithdraw: boolean
}

/**
 * The Monetization tab of the owner's cabinet: the referral link, AP balance
 * and total earned, withdrawal requests (handled by Arena managers) and the
 * AP the club's fans brought.
 */
const ClubMonetization = ({ canWithdraw }: ClubMonetizationProps) => {
  const t = useTranslations('club.monetization')
  const { data: finance } = useClubFinance()

  return (
    <section className={S.monetization}>
      <div className={S.top}>
        <div className={S.left}>
          <ReferralCard />
          <div className={S.balances}>
            <StatCard
              label={t('currentBalance')}
              value={ap(finance?.balance_ap)}
              hasGridBackground
            />
            <StatCard
              label={t('totalEarned')}
              value={ap(finance?.total_earned_ap)}
              hasGridBackground
            />
          </div>
        </div>
        <WithdrawHistory finance={finance} canWithdraw={canWithdraw} />
      </div>
      <FanActivity />
    </section>
  )
}

export default ClubMonetization
